import { InboxOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Modal, Upload } from 'antd';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from './apiError';
import { getAPIURL } from './host';
import { MusicSheetAsJSON } from './models/MusicSheet';

/** Imports a MusicXML file (paper, 7.2: "an option to import custom sheet music"). */
const ImportSheet = ({
  isOpen,
  onClose,
  onImported
}: {
  isOpen: boolean;
  onClose: () => void;
  onImported: (sheet: MusicSheetAsJSON) => void;
}) => {
  const { t: translate } = useTranslation();
  const [file, setFile] = useState<File>();
  const [title, setTitle] = useState('');
  const [composer, setComposer] = useState('');
  const [error, setError] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);

  const reset = () => {
    setFile(undefined);
    setTitle('');
    setComposer('');
    setError(undefined);
  };

  const submit = async () => {
    if (!file) return;
    setIsImporting(true);
    setError(undefined);
    try {
      const response = await axios.post<MusicSheetAsJSON>(
        `${getAPIURL()}/music-sheets`,
        await file.arrayBuffer(),
        {
          headers: { 'Content-Type': 'application/octet-stream' },
          params: {
            composer: composer || undefined,
            fileName: file.name,
            title: title || undefined
          }
        }
      );
      onImported(response.data);
      reset();
      onClose();
    } catch (reason) {
      setError(apiErrorMessage(reason));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal
      confirmLoading={isImporting}
      okButtonProps={{ disabled: !file }}
      okText={translate('import.submit')}
      onCancel={() => {
        reset();
        onClose();
      }}
      onOk={submit}
      open={isOpen}
      title={translate('import.title')}
    >
      <Form layout="vertical">
        <Form.Item label={translate('import.file')} required>
          <Upload.Dragger
            accept=".musicxml,.xml,.mxl"
            beforeUpload={(chosen) => {
              setFile(chosen);
              setError(undefined);
              // The file is sent on submit, not by the upload component.
              return false;
            }}
            fileList={file ? [{ name: file.name, status: 'done', uid: file.name }] : []}
            maxCount={1}
            onRemove={() => setFile(undefined)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{translate('import.drop')}</p>
            <p className="ant-upload-hint">{translate('import.hint')}</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          extra={translate('import.fromFile')}
          htmlFor="import-sheet-title"
          label={translate('import.titleLabel')}
        >
          <Input
            id="import-sheet-title"
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            value={title}
          />
        </Form.Item>
        <Form.Item
          extra={translate('import.fromFile')}
          htmlFor="import-sheet-composer"
          label={translate('import.composer')}
        >
          <Input
            id="import-sheet-composer"
            maxLength={120}
            onChange={(event) => setComposer(event.target.value)}
            value={composer}
          />
        </Form.Item>
        {error && <Alert showIcon title={error} type="error" />}
      </Form>
    </Modal>
  );
};

export default ImportSheet;
